import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { db } from "../../../firebase";
import { set, ref } from "firebase/database";
import { v4 as uuidv4 } from "uuid";
import {
  Box,
  createTheme,
  ThemeProvider,
  CssBaseline,
  Container,
  Tabs,
  Tab,
  Dialog,
  DialogContent,
  DialogActions,
  Typography,
  Button,
} from "@mui/material";
import Lottie from "../../../components/animation/LottieAnimation";
import { Formik } from "formik";
import { AsYouType, isSupportedCountry } from "libphonenumber-js";

import {
  decodeDataToken,
  decodeRefreshToken,
  deleteToken,
  getToken,
  verifyExpireTokenRefresh,
} from "../../../helpers/token/refreshToken";
import { addBooking } from "../../../features/booking/bookingSlice";
import SnackBar from "../../../components/snackbar/SnackBar";

import "./booking.css";
import { getModels } from "../../../api/resources/model";
import {
  dateFormat,
  formatDateTime,
  timeFormat,
} from "../../../helpers/date/date";
import { sendICSEmail, sendICSEmailNow } from "../../../api/resources/ics/ics";
import { CreateBackupUseCase } from "./usecases/backup/CreateBackupUseCase";
import loadingStyle from "./styles/loading.module.css";
import { BookingObserver } from "./usecases/BookingObserver";
import { DeleteBooking } from "./usecases/DeleteBooking";
import { SendNotificationsDriver } from "./usecases/SendNotificationsDriver";
import BookingFormFields from "./pages/components/BookingFormFields";
import OffersModal from "./pages/components/OffersModal";
import BookingsModal from "./pages/components/BookingsModal";
import useManagedBookingOffers from "./pages/hooks/useManagedBookingOffers";
import useBookingOfferIndicators from "./pages/hooks/useBookingOfferIndicators";
import OtherPersonModal from "./pages/components/OtherPersonModal";
import SubRoutesModal from "./pages/components/SubRoutesModal";
import OriginMapModal from "./pages/components/OriginMapModal";
import {
  getAddressKey,
  getAddressLabel,
  hasCoordinates,
} from "./pages/helpers/addressRouteHelpers";
import { useEnterpriseBookingAddresses } from "./pages/hooks/useEnterpriseBookingAddresses";
import { useSubRoutes } from "./pages/hooks/useSubRoutes";
import { calculateEnterpriseRoutePricing } from "./pages/services/enterpriseRoutePricingService";

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

const CheckSuccessAnimation = () => {
  return (
    <Box
      sx={{
        width: 150,
        height: 150,
        mb: 2,
      }}
    >
      <Lottie src="/assets/check_success.json" loop={true} autoplay />
    </Box>
  );
};

const BookingCreatedDialog = ({
  open,
  message,
  orderCounter,
  showViewHistory,
  onAccept,
  onViewHistory,
}) => (
  <Dialog open={open} onClose={onAccept} maxWidth="xs" fullWidth>
    <DialogContent
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
        pt: 4,
        pb: 2,
      }}
    >
      <CheckSuccessAnimation />
      <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1 }}>
        {message}
      </Typography>
      <Typography variant="body1" sx={{ fontWeight: "bold" }}>
        Numero de Pedido: {orderCounter}
      </Typography>
    </DialogContent>
    <DialogActions sx={{ justifyContent: "center", gap: 1, pb: 3 }}>
      {showViewHistory && (
        <Button variant="contained" color="primary" onClick={onViewHistory}>
          Ver Historial
        </Button>
      )}
      <Button variant="outlined" color="primary" onClick={onAccept}>
        Aceptar
      </Button>
    </DialogActions>
  </Dialog>
);

BookingCreatedDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  message: PropTypes.string.isRequired,
  orderCounter: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  showViewHistory: PropTypes.bool.isRequired,
  onAccept: PropTypes.func.isRequired,
  onViewHistory: PropTypes.func.isRequired,
};

const BOOKING_TYPE_NOW = 1;
const BOOKING_TYPE_LATER = 2;
const BOOKING_TYPE_PER_HOURS = 3;
const BOOKING_TYPE_QUOTE = 4;
const PERSISTED_QUOTE_BOOKING_TYPE = 2;
const RIDER_OPTION_ME = 1;
const RIDER_OPTION_OTHER_PERSON = 2;
const RIDER_OPTION_LABELS = {
  [RIDER_OPTION_ME]: "ME",
  [RIDER_OPTION_OTHER_PERSON]: "OTHER_PERSON",
};
const COMPANY_ID = 1;
const CURRENCY_PEN_ID = 1;
const CURRENCY_PEN_LABEL = "PEN";
const DEFAULT_ZERO_AMOUNT = 0;
const PAYMENT_TYPE_CASH = "EFECTIVO";
const PAYMENT_TYPE_CREDIT = "CREDITO";
const STATUS_PENDING = "PENDIENTE";
const CREATED_BY_BUSINESS = "BUSINESS";
const SUPPORT_PHONE = "958200900";
const SUB_ROUTES_GROUP = "booking-sub-routes";
const DEFAULT_PHONE_COUNTRY = "PE";
const DEFAULT_PHONE_DIAL_CODE = "+51";
const INVALID_FIELDS = {
  riderOption: "riderOption",
  origin: "origin",
  destination: "destination",
  persons: "persons",
  vehicle: "vehicle",
  booking: "booking",
  date: "date",
  time: "time",
  dateEnd: "dateEnd",
  timeEnd: "timeEnd",
  otherPersonName: "otherPersonName",
  otherPersonPhone: "otherPersonPhone",
};
const SNACKBAR_MESSAGES = {
  selectSubRoute: "SELECCIONE UNA SUB RUTA",
  duplicateSubRoute: "NO SE PERMITEN DOS SUBRUTAS IGUALES CONSECUTIVAS",
  missingGoogleMapsKey: "NO SE ENCONTRO LA API KEY DE GOOGLE MAPS",
  routeCalculationFailed: "NO SE PUDO CALCULAR LA RUTA",
  selectDateTime: "SELECCIONE LA FECHA",
  selectTimeTime: "SELECCIONE LA HORA",
  selectRider: "SELECCIONE PARA QUIEN ES EL SERVICIO",
  selectBookingType: "SELECCIONE PARA CUANDO ES EL SERVICIO",
  selectVehicle: "SELECCIONE EL TIPO DE VEHICULO",
  selectPersons: "INGRESE LA CANTIDAD DE PASAJEROS",
  missingDestination: "INGRESE EL DESTINO DEL SERVICIO",
  missingOrigin: "INGRESE EL ORIGEN DEL SERVICIO",
  bookingCreated: "PEDIDO CREADO, VER SU HISTORIAL",
  bookingCreatedEmailFailed: "PEDIDO CREADO, PERO FALLO EL ENVIO DE EMAIL",
  missingOtherPersonName: "INGRESE NOMBRE DE INVITADO",
  incompleteOtherPersonName: "INGRESE NOMBRE Y APELLIDO DE INVITADO",
  missingOtherPersonPhone: "INGRESE TELEFONO DE INVITADO",
  selectEndDateTime: "SELECCIONE LA FECHA Y HORA DE FIN",
  bookingNowCreated: "PEDIDO CREADO CORRECTAMENTE",
  bookingLaterCreated: "RESERVACION CREADA CORRECTAMENTE",
  bookingPerHoursCreated: "PEDIDO POR HORAS CREADO CORRECTAMTENTE",
  bookingQuoteCreated: "PEDIDO DE COTIZACION FUE CREADO CORRECTAMENTE",
  pastDateNotAllowed: "NO SE PERMITE SELECCIONAR UNA FECHA PASADA",
};

const DEFAULT_BOOKING_FORM_VALUES = {
  persons: 1,
  vehicle: "",
  booking: 0,
  note: "",
  riderOption: 0,
  riderOptionName: "",
  otherPersonName: "",
  otherPersonPhone: "",
};

const formatPricePEN = (price) => {
  const numericPrice = Number(price);
  const safePrice = Number.isFinite(numericPrice) ? numericPrice : 0;
  return `${safePrice.toFixed(2)} ${CURRENCY_PEN_LABEL}`;
};

const formatPhoneByCountry = (phone, countryCode) => {
  const formatterCountry = isSupportedCountry(countryCode)
    ? countryCode
    : DEFAULT_PHONE_COUNTRY;

  return new AsYouType(formatterCountry).input(phone);
};

const getBookingCreatedMessage = (bookingData) => {
  if (bookingData.booking_type === BOOKING_TYPE_NOW) {
    return SNACKBAR_MESSAGES.bookingNowCreated;
  }
  if (bookingData.booking_type === BOOKING_TYPE_PER_HOURS) {
    return SNACKBAR_MESSAGES.bookingPerHoursCreated;
  }
  if (bookingData.booking_type === BOOKING_TYPE_QUOTE) {
    return SNACKBAR_MESSAGES.bookingQuoteCreated;
  }

  return SNACKBAR_MESSAGES.bookingLaterCreated;
};

const isPastDate = (selectedDate) => {
  if (!selectedDate?.$d) return false;

  const dateToValidate = new Date(selectedDate.$d);
  dateToValidate.setHours(0, 0, 0, 0);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return dateToValidate < today;
};

const priceSummarySx = {
  height: 45,
  border: "1px solid #000",
  borderRadius: 1,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontWeight: "bold",
};

const Booking = () => {
  const [value, setValue] = useState(0);
  const { t } = useTranslation();
  const theme = createTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const bookingState = useSelector((state) => state.bookings);
  const [model, setModel] = useState([]);
  const [date, setDate] = useState(null);
  const [time, setTime] = useState(null);

  const [dateEnd, setDateEnd] = useState(null);
  const [timeEnd, setTimeEnd] = useState(null);

  const [priceTotal, setPriceTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [stateSnk, setStateSnk] = useState(false);
  const [severity, setSeverity] = useState("success");
  const [messageSnk, setMessageSnk] = useState("");
  const [snackOffer, setSnackOffer] = useState(null);
  const [invalidField, setInvalidField] = useState("");
  const [openOriginMapModal, setOpenOriginMapModal] = useState(false);
  const [openDestinationMapModal, setOpenDestinationMapModal] = useState(false);
  const [openSubRouteMapModal, setOpenSubRouteMapModal] = useState(false);
  const [openBookingCreatedDialog, setOpenBookingCreatedDialog] =
    useState(false);
  const [createdOrderCounter, setCreatedOrderCounter] = useState("");
  const [createdBookingMessage, setCreatedBookingMessage] = useState(
    SNACKBAR_MESSAGES.bookingNowCreated,
  );
  const [showBookingHistoryButton, setShowBookingHistoryButton] =
    useState(false);
  const [historyInitialTab, setHistoryInitialTab] = useState(2);
  const isSubmittingBookingRef = useRef(false);
  const vertical = "top";
  const horizontal = "center";

  const handleSnkClose = () => setStateSnk(false);

  const snackBarDialog = useCallback((message, severity = "success") => {
    setSnackOffer(null);
    setMessageSnk(message);
    setSeverity(severity);
    setStateSnk(true);
  }, []);

  const handleAcceptBookingCreatedDialog = () => {
    setOpenBookingCreatedDialog(false);
  };

  const handleViewBookingHistory = () => {
    setOpenBookingCreatedDialog(false);
    navigate("/history-enterprise", {
      state: {
        initialTab: historyInitialTab,
        source: "booking-created-dialog",
      },
    });
  };

  const clearInvalidField = (fieldName) => {
    setInvalidField((currentField) =>
      currentField === fieldName ? "" : currentField,
    );
  };

  const createMapAddress = ({ address, point }) => ({
    name: address,
    latitude: point.lat,
    longitude: point.lng,
    url: "",
    url_address: "",
  });

  const getPersistedSubRoutes = () =>
    subRoutesList.slice(0, -1).map((route, index) => ({
      id: index + 1,
      lat: Number(route.latitude),
      lng: Number(route.longitude),
      name: String(route.name || "").toUpperCase(),
    }));

  const handleOpenOriginMapModal = () => {
    setOpenOriginMapModal(true);
  };

  const handleCloseOriginMapModal = () => {
    setOpenOriginMapModal(false);
  };

  const handleOpenDestinationMapModal = () => {
    setOpenDestinationMapModal(true);
  };

  const handleCloseDestinationMapModal = () => {
    setOpenDestinationMapModal(false);
  };

  const handleOpenSubRouteMapModal = () => {
    setOpenSubRouteMapModal(true);
  };

  const handleCloseSubRouteMapModal = () => {
    setOpenSubRouteMapModal(false);
  };

  const handleAcceptOriginMapLocation = (location) => {
    clearInvalidField(INVALID_FIELDS.origin);
    setAddressOrigin(createMapAddress(location));
    handleCloseOriginMapModal();
  };

  const handleAcceptDestinationMapLocation = (location) => {
    clearInvalidField(INVALID_FIELDS.destination);
    handleDestinationChange(createMapAddress(location));
    handleCloseDestinationMapModal();
  };

  const handleAcceptSubRouteMapLocation = (location) => {
    handleAddSubRoute(createMapAddress(location));
    handleCloseSubRouteMapModal();
  };

  const { addressApi, addressOrigin, setAddressOrigin, paymentType } =
    useEnterpriseBookingAddresses();
  const tokenPaymentType = useMemo(() => {
    try {
      const dataToken = getToken("dttkn");
      if (!dataToken) return paymentType;

      const { payment_type } = decodeDataToken(dataToken);
      return payment_type || paymentType;
    } catch (error) {
      console.log(error);
      return paymentType;
    }
  }, [paymentType]);

  //OBSERVERS DE BOOKING
  const [bookings, setBookings] = useState([]);
  const [selectedBookingId, setSelectedBookingId] = useState("");
  const [bookingsLoading, setBookingsLoading] = useState(true);
  const [bookingsError, setBookingsError] = useState("");
  const bookingNow = bookings.find((booking) => booking.uuid === selectedBookingId) || null;
  const sessionToken = getToken("etknre");
  const actorId = sessionToken ? decodeRefreshToken(sessionToken) : null;
  const managedOffers = useManagedBookingOffers(bookingNow, sessionToken, actorId);
  const handleNewBookingOffers = useCallback((booking, count) => {
    snackBarDialog(
      `${count === 1 ? "Nueva oferta" : `${count} nuevas ofertas`} para el pedido TM-${booking.counterBooking}`,
      "info",
    );
    setSnackOffer({ bookingId: booking.uuid, key: uuidv4() });
  }, [snackBarDialog]);
  const offerIndicators = useBookingOfferIndicators(bookings, sessionToken, actorId, handleNewBookingOffers);
  const offersList = managedOffers.offers;
  const driverSelect = managedOffers.driver;
  const isBookingActive = bookings.length > 0;

  const handleChange = (event, newValue) => setValue(newValue);

  useEffect(() => {
    const models = async () => {
      try {
        const response = await getModels();
        const data = response.data.filter((model) => model.id);
        setModel(data);
      } catch (error) {
        console.log(error);
      }
    };
    models();
  }, []);
  const {
    addressDestination,
    serviceList,
    subRoutesList,
    openDestinationSelect,
    setOpenDestinationSelect,
    openSubRoutesModal,
    subRouteSelected,
    destinationInputValue,
    setDestinationInputValue,
    addressDestinationText,
    handleDestinationChange,
    handleOpenSubRoutesModal,
    handleCloseSubRoutesModal,
    handleAddSubRoute,
    handleRemoveSubRoute,
    handleSubRoutesDragEnd,
    resetSubRoutes,
  } = useSubRoutes({
    bookingData: bookingState?.data,
    onError: (message) => snackBarDialog(message, "error"),
    messages: {
      selectSubRoute: SNACKBAR_MESSAGES.selectSubRoute,
      duplicateSubRoute: SNACKBAR_MESSAGES.duplicateSubRoute,
    },
  });

  useEffect(() => {
    let isMounted = true;

    const updateRoutePricing = async () => {
      try {
        const routePricing = await calculateEnterpriseRoutePricing({
          addressOrigin,
          subRoutesList,
        });

        if (!isMounted) return;
        setPriceTotal(routePricing?.total ?? 0);
      } catch (error) {
        console.log(error);
        if (!isMounted) return;
        setPriceTotal(0);
        snackBarDialog(SNACKBAR_MESSAGES.routeCalculationFailed, "error");
      }
    };

    updateRoutePricing();

    return () => {
      isMounted = false;
    };
  }, [addressOrigin, subRoutesList, snackBarDialog]);

  const initialValues = bookingState?.data
    ? bookingState?.data
    : { ...DEFAULT_BOOKING_FORM_VALUES };

  const checkTime = (booking) => {
    if (booking !== BOOKING_TYPE_NOW) {
      const today = new Date();
      const { day, month, year, hour, minute } = formatDateTime(
        date.$d,
        time.$d,
      );

      const dateOrder = new Date(year, month, day, hour, minute, 0);
      const diffInMillisec = dateOrder.getTime() - today.getTime();
      const diffInMin = diffInMillisec / (1000 * 60);

      if (diffInMin <= 30) {
        snackBarDialog("TODA RESERVA DEBE SER MAYOR A 30 MINUTOS", "error");
        return;
      }
      return true;
    }

    return true;
  };

  const validateBookingData = (values) => {
    if (!riderOption) {
      return {
        field: INVALID_FIELDS.riderOption,
        message: SNACKBAR_MESSAGES.selectRider,
      };
    }
    if (riderOption === RIDER_OPTION_OTHER_PERSON) {
      if (!otherPersonPreview.name) {
        return {
          field: INVALID_FIELDS.otherPersonName,
          message: SNACKBAR_MESSAGES.missingOtherPersonName,
        };
      }
      if (!otherPersonPreview.phone) {
        return {
          field: INVALID_FIELDS.otherPersonPhone,
          message: SNACKBAR_MESSAGES.missingOtherPersonPhone,
        };
      }
    }

    if (!hasCoordinates(addressOrigin)) {
      return {
        field: INVALID_FIELDS.origin,
        message: SNACKBAR_MESSAGES.missingOrigin,
      };
    }
    if (!hasCoordinates(addressDestination)) {
      return {
        field: INVALID_FIELDS.destination,
        message: SNACKBAR_MESSAGES.missingDestination,
      };
    }
    if (!Number(values.persons) || Number(values.persons) <= 0) {
      return {
        field: INVALID_FIELDS.persons,
        message: SNACKBAR_MESSAGES.selectPersons,
      };
    }
    if (!values.vehicle) {
      return {
        field: INVALID_FIELDS.vehicle,
        message: SNACKBAR_MESSAGES.selectVehicle,
      };
    }
    if (!values.booking) {
      return {
        field: INVALID_FIELDS.booking,
        message: SNACKBAR_MESSAGES.selectBookingType,
      };
    }
    if (values.booking !== BOOKING_TYPE_NOW && !date) {
      return {
        field: INVALID_FIELDS.date,
        message: SNACKBAR_MESSAGES.selectDateTime,
      };
    }
    if (values.booking !== BOOKING_TYPE_NOW && isPastDate(date)) {
      return {
        field: INVALID_FIELDS.date,
        message: SNACKBAR_MESSAGES.pastDateNotAllowed,
      };
    }
    if (values.booking !== BOOKING_TYPE_NOW && !time) {
      return {
        field: INVALID_FIELDS.time,
        message: SNACKBAR_MESSAGES.selectTimeTime,
      };
    }
    if (values.booking === BOOKING_TYPE_PER_HOURS && !dateEnd) {
      return {
        field: INVALID_FIELDS.dateEnd,
        message: SNACKBAR_MESSAGES.selectEndDateTime,
      };
    }
    if (values.booking === BOOKING_TYPE_PER_HOURS && isPastDate(dateEnd)) {
      return {
        field: INVALID_FIELDS.dateEnd,
        message: SNACKBAR_MESSAGES.pastDateNotAllowed,
      };
    }
    if (values.booking === BOOKING_TYPE_PER_HOURS && !timeEnd) {
      return {
        field: INVALID_FIELDS.timeEnd,
        message: SNACKBAR_MESSAGES.selectEndDateTime,
      };
    }

    return null;
  };

  const handleFormSubmit = async (values, { resetForm }) => {
    if (isSubmittingBookingRef.current) return;

    isSubmittingBookingRef.current = true;

    try {
      const validationError = validateBookingData(values);
      if (validationError) {
        setInvalidField(validationError.field);
        if (
          riderOption === RIDER_OPTION_OTHER_PERSON &&
          (validationError.field === INVALID_FIELDS.otherPersonName ||
            validationError.field === INVALID_FIELDS.otherPersonPhone)
        ) {
          setOpenOtherPersonModal(true);
        }
        snackBarDialog(validationError.message, "error");
        return;
      }
      setInvalidField("");

      const verify = verifyExpireTokenRefresh();
      if (!verify) {
        if (checkTime(values.booking)) {
          setIsLoading(true);

          const uuid = uuidv4();
          const nDate = new Date(Date.now());

          const data = createDataMySql(values, uuid, nDate);
          const backup = await CreateBackupUseCase(data);
          if (backup.message === "success") {
            const orderNumber = backup.insertId;
            const fData = createDataFirebase(values, uuid, orderNumber, nDate);
            await createBookingFirebase(fData, resetForm);
            
            if (values.booking === BOOKING_TYPE_NOW) {
              await SendNotificationsDriver();
            }
          }
        }
      } else {
        deleteToken("etknre");
        const origin = {
          name: addressOrigin.name,
          lat: addressOrigin.latitude,
          lng: addressOrigin.longitude,
        };
        const destination = {
          name: addressDestination.name,
          lat: addressDestination.latitude,
          lng: addressDestination.longitude,
        };
        dispatch(
          addBooking({
            ...values,
            ...getSelectedRiderData(),
            id: uuidv4(),
            origin,
            destination,
            serviceList,
          }),
        );
        navigate("/login");
      }
    } finally {
      isSubmittingBookingRef.current = false;
      setIsLoading(false);
    }
  };

  const createDataFirebase = (values, uuid, counter, nDate) => {
    const token = getToken("etknre");
    const uid = decodeRefreshToken(token);
    const clientToken = getToken("dttkn");
    const { name, lastname, email, phone, enterprise_id, commertial_name } =
      decodeDataToken(clientToken);

    const data = {
      uuid,
      client: Number(uid),
      clientName: `${name} ${lastname}`,
      clientEmail: email,
      clientPhone: phone,
      enterpriseId: enterprise_id,
      enterpriseName: commertial_name,
      origin: {
        name: addressOrigin.name,
        lat: addressOrigin.latitude,
        lng: addressOrigin.longitude,
        url: addressOrigin.url,
      },
      destination: {
        name: addressDestination.name,
        lat: addressDestination.latitude,
        lng: addressDestination.longitude,
        url: addressDestination.url,
      },
      subRoutes: getPersistedSubRoutes(),
      intermediate: serviceList,
      ...getSelectedRiderData(),

      persons: values.persons,
      vehicle: values.vehicle,
      date:
        values.booking === BOOKING_TYPE_NOW
          ? dateFormat(nDate)
          : dateFormat(date.$d),
      time:
        values.booking === BOOKING_TYPE_NOW
          ? timeFormat(nDate)
          : timeFormat(time.$d),
      dateEnd:
        values.booking === BOOKING_TYPE_PER_HOURS ? dateFormat(dateEnd.$d) : "",
      timeEnd:
        values.booking === BOOKING_TYPE_PER_HOURS ? timeFormat(timeEnd.$d) : "",
      note: values.note ? values.note.toUpperCase() : "",
      noteAdmin: "",
      currency: CURRENCY_PEN_ID,
      payment_type: paymentType === PAYMENT_TYPE_CASH ? 1 : 2,
      cotizar:
        values.booking === BOOKING_TYPE_QUOTE
          ? false
          : paymentType === PAYMENT_TYPE_CASH
            ? false
            : true,
      payment_voucher: 1,
      tax: DEFAULT_ZERO_AMOUNT,
      toll: 0,
      price: DEFAULT_ZERO_AMOUNT,
      total: Number(priceTotal),
      discount: 0,
      advance: 0,
      booking_type:
        values.booking === BOOKING_TYPE_QUOTE
          ? PERSISTED_QUOTE_BOOKING_TYPE
          : values.booking,
      statusService: STATUS_PENDING,
      statusPayment: STATUS_PENDING,
      statusBooking: STATUS_PENDING,
      createBooking: CREATED_BY_BUSINESS,
      counterBooking: counter,
      created: Date.now(),
      isCreated: false,
      companyId: COMPANY_ID,
    };

    return data;
  };

  const createDataMySql = (values, uuid, nDate) => {
    const token = getToken("etknre");
    const uid = decodeRefreshToken(token);

    return {
      uuid,
      userType: 0, // 0 para empresas 1 para clientes
      originName: addressOrigin.name,
      originLat: addressOrigin.latitude,
      originLng: addressOrigin.longitude,
      originUrl: "",
      destinationName: addressDestination.name,
      destinationLat: addressDestination.latitude,
      destinationLng: addressDestination.longitude,
      destinationUrl: "",
      subRoutes: getPersistedSubRoutes(),
      ...getSelectedRiderData(),
      date:
        values.booking === BOOKING_TYPE_NOW
          ? dateFormat(nDate)
          : dateFormat(date.$d),
      time:
        values.booking === BOOKING_TYPE_NOW
          ? timeFormat(nDate)
          : timeFormat(time.$d),
      dateEnd:
        values.booking === BOOKING_TYPE_PER_HOURS ? dateFormat(dateEnd.$d) : "",
      timeEnd:
        values.booking === BOOKING_TYPE_PER_HOURS ? timeFormat(timeEnd.$d) : "",
      persons: values.persons,
      vehicleModel: values.vehicle,
      notes: values.note ? values.note.toUpperCase() : "",
      notesAdmin: "",
      userId: uid,
      driverId: 0,
      vehicleId: 0,
      paymentId: 0,
      bookingType:
        values.booking === BOOKING_TYPE_QUOTE
          ? PERSISTED_QUOTE_BOOKING_TYPE
          : values.booking,
      tourId: 0, //values.booking === BOOKING_TYPE_QUOTE ? tour.tour_id : "",
      invoiceId: 0,
      createdBy: CREATED_BY_BUSINESS,
      status: 1,
      statePayment: STATUS_PENDING,
      assigned: false,
      invoice: false,
      invoiceStatus: false,
      travelStatus: 1,
      currency: CURRENCY_PEN_ID,
      paymentType: 1,
      paymentVoucher: 1,
      tax: DEFAULT_ZERO_AMOUNT,
      toll: 0,
      price: DEFAULT_ZERO_AMOUNT,
      total: priceTotal,
      discount: 0,
      advance: 0,
      quote: values.booking !== BOOKING_TYPE_QUOTE,
      companyId: COMPANY_ID,
    };
  };

  const createBookingFirebase = async (data, resetForm) => {
    const dbRef = `BookingBusiness/${data.uuid}`;

    return set(ref(db, dbRef), data)
      .then(async () => {
        const bookingCreatedMessage = getBookingCreatedMessage(data);

        snackBarDialog(bookingCreatedMessage);
        setCreatedOrderCounter(data.counterBooking);
        setCreatedBookingMessage(bookingCreatedMessage);
        if (data.booking_type !== BOOKING_TYPE_NOW) {
          setHistoryInitialTab(data.quote ? 2 : 1);
          setShowBookingHistoryButton(true);
          setOpenBookingCreatedDialog(true);
        }

        try {
          if (data.booking_type === PERSISTED_QUOTE_BOOKING_TYPE) {
            const newBooking = {
              counterBooking: data.counterBooking,
              clientName: data.clientName,
              clientEmail: data.clientEmail,
              clientPhone: data.clientPhone,
              origin: { name: addressOrigin.name },
              destination: { name: addressDestination.name },
              date: dateFormat(date.$d),
              time: timeFormat(time.$d),
              note: data.note ? data.note.toUpperCase() : "",
              persons: data.persons,
              vehicle: data.vehicle,
              web: "Business",
            };
            await sendICSEmail("token", newBooking);
          } else if (data.booking_type === BOOKING_TYPE_NOW) {
            setSelectedBookingId("");
            setOpenOffers(true);
            const dataEmail = {
              pedido: data.counterBooking,
              name: data.clientName,
              email: data.clientEmail,
              telefono: data.clientPhone,
              origen: data.origin.name,
              destino: data.destination.name,
              date: data.date,
              time: data.time,
              notas: data.note ? data.note.toUpperCase() : "",
              persons: data.persons,
              vehicle: data.vehicle,
              web: "Business",
            };
            await sendICSEmailNow("token", dataEmail);
          }
        } catch (error) {
          snackBarDialog(SNACKBAR_MESSAGES.bookingCreatedEmailFailed, "error");
        }

        setAddressOrigin(null);
        resetSubRoutes();
        resetRiderData();
        resetForm({
          values: DEFAULT_BOOKING_FORM_VALUES,
        });
        setDate(null);
        setTime(null);
        setDateEnd(null);
        setTimeEnd(null);
        setPriceTotal(0);
        setInvalidField("");
        setDestinationInputValue("");
        setOpenDestinationSelect(false);
        setOpenOtherPersonModal(false);
      })
      .catch((error) => {
        console.log(error);
        snackBarDialog("Error: No se creo el Pedido", "error");
      });
  };

  const [openOffers, setOpenOffers] = useState(false);
  const handleCloseOffer = () => setOpenOffers(false);
  const openBookingsList = () => {
    setSelectedBookingId("");
    setOpenOffers(true);
  };

  const handleViewNotifiedBooking = () => {
    if (!snackOffer) return;
    const bookingExists = bookings.some((booking) => booking.uuid === snackOffer.bookingId);
    setSelectedBookingId(bookingExists ? snackOffer.bookingId : "");
    setOpenOffers(true);
    setStateSnk(false);
    setSnackOffer(null);
  };

  useEffect(() => {
    if (!actorId) {
      setBookings([]);
      setBookingsLoading(false);
      return undefined;
    }
    setBookings([]);
    setSelectedBookingId("");
    setBookingsLoading(true);
    return BookingObserver(actorId, (list) => {
      setBookings(list);
      setBookingsLoading(false);
      setBookingsError("");
      setSelectedBookingId((selected) => list.some((item) => item.uuid === selected) ? selected : "");
    }, () => {
      setBookingsLoading(false);
      setBookingsError("No se pudo cargar la lista de pedidos.");
    });
  }, [actorId]);

  const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    // height: "70vh",
    border: "1px solid #000",
    borderRadius: "10px",
    boxShadow: 24,
    p: 1,
  };

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  const cancelBooking = async () => {
    if (!bookingNow || managedOffers.busy || Number(bookingNow.idDriver) > 0 ||
        ["ASSIGNED", "CLOSED"].includes(managedOffers.state)) {
      throw new Error("El pedido ya no está disponible para cancelar la búsqueda.");
    }
    await DeleteBooking(bookingNow.firebaseKey || bookingNow.uuid);
    setSelectedBookingId("");
  };

  const [riderOption, setRiderOption] = useState(
    bookingState?.data?.riderOption ?? 0,
  );
  const [openOtherPersonModal, setOpenOtherPersonModal] = useState(false);
  const [otherPersonName, setOtherPersonName] = useState(
    bookingState?.data?.otherPersonName ?? "",
  );
  const [otherPersonPhone, setOtherPersonPhone] = useState(
    bookingState?.data?.otherPersonPhone ?? "",
  );
  const [otherPersonPhoneCountry, setOtherPersonPhoneCountry] = useState(
    DEFAULT_PHONE_COUNTRY,
  );
  const [phoneCountries, setPhoneCountries] = useState([]);
  const [otherPersonPreview, setOtherPersonPreview] = useState({
    name: bookingState?.data?.otherPersonName ?? "",
    phone: bookingState?.data?.otherPersonPhone ?? "",
  });
  const selectedPhoneCountry = useMemo(
    () =>
      phoneCountries.find(
        (country) => country.code === otherPersonPhoneCountry,
      ),
    [phoneCountries, otherPersonPhoneCountry],
  );

  useEffect(() => {
    fetch("/assets/countries.json")
      .then((response) => response.json())
      .then((countries) => {
        if (Array.isArray(countries)) {
          setPhoneCountries(countries);
        }
      })
      .catch(() => {
        setPhoneCountries([]);
      });
  }, []);

  const getSelectedRiderData = () => {
    const isOtherPerson = riderOption === RIDER_OPTION_OTHER_PERSON;

    return {
      riderOption,
      riderOptionName: RIDER_OPTION_LABELS[riderOption] ?? "",
      otherPersonName: isOtherPerson ? otherPersonPreview.name : "",
      otherPersonPhone: isOtherPerson ? otherPersonPreview.phone : "",
    };
  };
  const resetRiderData = () => {
    setRiderOption(0);
    setOtherPersonName("");
    setOtherPersonPhone("");
    setOtherPersonPreview({
      name: "",
      phone: "",
    });
    setOtherPersonPhoneCountry(DEFAULT_PHONE_COUNTRY);
  };
  const handleRiderOptionChange = (event) => {
    const value = Number(event.target.value);
    clearInvalidField(INVALID_FIELDS.riderOption);
    setRiderOption(value);
    if (value !== RIDER_OPTION_OTHER_PERSON) {
      setOtherPersonName("");
      setOtherPersonPhone("");
      setOtherPersonPreview({
        name: "",
        phone: "",
      });
      setOtherPersonPhoneCountry(DEFAULT_PHONE_COUNTRY);
    }
  };
  const handleCloseOtherPersonModal = () => {
    setOpenOtherPersonModal(false);
  };
  const handleOpenOtherPersonModal = () => {
    setOpenOtherPersonModal(true);
  };

  const handleOtherPersonNameChange = (event) => {
    clearInvalidField(INVALID_FIELDS.otherPersonName);
    const value = event.target.value
      .toUpperCase()
      .trimStart()
      .replace(/\s+/g, " ");
    setOtherPersonName(value);
  };

  const handleOtherPersonPhoneChange = (event) => {
    clearInvalidField(INVALID_FIELDS.otherPersonPhone);
    const rawValue = event.target.value.replace(/[^\d]/g, "");
    const value = formatPhoneByCountry(rawValue, otherPersonPhoneCountry);
    setOtherPersonPhone(value);
  };

  const handleOtherPersonPhoneCountryChange = (event) => {
    const countryCode = event.target.value;
    const rawValue = otherPersonPhone.replace(/[^\d]/g, "");
    clearInvalidField(INVALID_FIELDS.otherPersonPhone);
    setOtherPersonPhoneCountry(countryCode);
    setOtherPersonPhone(formatPhoneByCountry(rawValue, countryCode));
  };

  const handleAcceptOtherPerson = () => {
    const name = otherPersonName.trim();
    const nameWords = name.split(/\s+/).filter(Boolean);

    if (!name) {
      snackBarDialog(SNACKBAR_MESSAGES.missingOtherPersonName, "error");
      return;
    }
    if (nameWords.length < 2) {
      snackBarDialog(SNACKBAR_MESSAGES.incompleteOtherPersonName, "error");
      setInvalidField(INVALID_FIELDS.otherPersonName);
      return;
    }
    if (!otherPersonPhone.trim()) {
      snackBarDialog(SNACKBAR_MESSAGES.missingOtherPersonPhone, "error");
      return;
    }
    const phoneNumber = otherPersonPhone.trim();
    const phoneDialCode =
      selectedPhoneCountry?.dial_code ?? DEFAULT_PHONE_DIAL_CODE;
    const phone = `${phoneDialCode} ${phoneNumber}`.trim();
    setOtherPersonName(name);
    setOtherPersonPhone(phoneNumber);
    setOtherPersonPreview({
      name,
      phone,
    });
    setInvalidField("");
    handleCloseOtherPersonModal();
  };

  return (
    <ThemeProvider theme={theme}>
      <Container component="main" maxWidth="md">
        <CssBaseline />
        <Box mb={3} sx={{ width: "100%" }}>
          <Box
            sx={{ borderBottom: 1, borderColor: "divider", marginBottom: 2 }}
          >
            <Tabs
              value={value}
              onChange={handleChange}
              aria-label="Booking-Tabs"
              centered
              textColor="primary"
              indicatorColor="primary"
            >
              <Tab
                label={t("Bookings")}
                {...a11yProps(0)}
                sx={{ color: "#000000", fontSize: 18 }}
              />
            </Tabs>
          </Box>
          <TabPanel value={value} index={0}>
            <Formik onSubmit={handleFormSubmit} initialValues={initialValues}>
              {({
                values,
                errors,
                touched,
                handleBlur,
                handleChange,
                handleSubmit,
                setFieldValue,
              }) => (
                <form onSubmit={handleSubmit}>
                  <Box onSubmit={handleSubmit} sx={{ mt: 1, margin: "5px" }}>
                    <BookingFormFields
                      values={values}
                      errors={errors}
                      touched={touched}
                      handleBlur={handleBlur}
                      handleChange={handleChange}
                      setFieldValue={setFieldValue}
                      t={t}
                      riderOption={riderOption}
                      riderOptions={{
                        me: RIDER_OPTION_ME,
                        otherPerson: RIDER_OPTION_OTHER_PERSON,
                      }}
                      handleRiderOptionChange={handleRiderOptionChange}
                      handleOpenOtherPersonModal={handleOpenOtherPersonModal}
                      otherPersonPreview={otherPersonPreview}
                      addressApi={addressApi}
                      addressOrigin={addressOrigin}
                      setAddressOrigin={setAddressOrigin}
                      addressDestination={addressDestination}
                      addressDestinationText={addressDestinationText}
                      destinationInputValue={destinationInputValue}
                      setDestinationInputValue={setDestinationInputValue}
                      openDestinationSelect={openDestinationSelect}
                      setOpenDestinationSelect={setOpenDestinationSelect}
                      handleOpenSubRoutesModal={handleOpenSubRoutesModal}
                      handleDestinationChange={handleDestinationChange}
                      handleOpenOriginMapModal={handleOpenOriginMapModal}
                      handleOpenDestinationMapModal={
                        handleOpenDestinationMapModal
                      }
                      getAddressLabel={getAddressLabel}
                      model={model}
                      bookingTypes={{
                        now: BOOKING_TYPE_NOW,
                        later: BOOKING_TYPE_LATER,
                        perHours: BOOKING_TYPE_PER_HOURS,
                        quote: BOOKING_TYPE_QUOTE,
                      }}
                      date={date}
                      setDate={setDate}
                      time={time}
                      setTime={setTime}
                      dateEnd={dateEnd}
                      setDateEnd={setDateEnd}
                      timeEnd={timeEnd}
                      setTimeEnd={setTimeEnd}
                      priceTotal={priceTotal}
                      formatPrice={formatPricePEN}
                      priceSummarySx={priceSummarySx}
                      isBookingActive={isBookingActive}
                      isLoading={isLoading}
                      setOpenOffers={openBookingsList}
                      bookingCount={bookings.length}
                      loaderClassName={loadingStyle.loader}
                      invalidField={invalidField}
                      invalidFields={INVALID_FIELDS}
                      clearInvalidField={clearInvalidField}
                      showPrice={tokenPaymentType !== PAYMENT_TYPE_CREDIT}
                    />
                  </Box>
                  <SnackBar
                    key={snackOffer?.key || "booking-message"}
                    stateSnk={stateSnk}
                    vertical={vertical}
                    horizontal={horizontal}
                    duration={snackOffer ? 10000 : 4000}
                    handleClose={handleSnkClose}
                    severity={severity}
                    messageSnk={messageSnk}
                    action={snackOffer ? (
                      <Button color="inherit" size="small" onClick={handleViewNotifiedBooking}>
                        Ver
                      </Button>
                    ) : undefined}
                  />
                </form>
              )}
            </Formik>
          </TabPanel>

          <BookingsModal
            open={openOffers && !bookingNow}
            onClose={handleCloseOffer}
            bookings={bookings}
            offerIndicators={offerIndicators}
            loading={bookingsLoading}
            error={bookingsError}
            onView={(booking) => setSelectedBookingId(booking.uuid)}
          />
          <OffersModal
            key={selectedBookingId}
            open={openOffers && Boolean(bookingNow)}
            onClose={handleCloseOffer}
            modalStyle={modalStyle}
            bookingNow={bookingNow}
            driverSelect={driverSelect}
            cancelBooking={cancelBooking}
            offersList={offersList}
            acceptCounterOffer={managedOffers.accept}
            rejectCounterOffer={managedOffers.reject}
            offersLoading={managedOffers.loading}
            offersError={managedOffers.error}
            offersBusy={managedOffers.busy}
            waitingProjection={managedOffers.waitingProjection}
            offersState={managedOffers.state}
            onRefreshOffers={managedOffers.refresh}
            onBack={() => setSelectedBookingId("")}
            loaderClassName={loadingStyle.loader}
            supportPhone={SUPPORT_PHONE}
          />

          <SubRoutesModal
            open={openSubRoutesModal}
            onClose={handleCloseSubRoutesModal}
            addressOrigin={addressOrigin}
            subRoutesList={subRoutesList}
            handleSubRoutesDragEnd={handleSubRoutesDragEnd}
            getAddressKey={getAddressKey}
            getAddressLabel={getAddressLabel}
            handleRemoveSubRoute={handleRemoveSubRoute}
            subRoutesGroup={SUB_ROUTES_GROUP}
            subRouteSelected={subRouteSelected}
            addressApi={addressApi}
            handleAddSubRoute={handleAddSubRoute}
            handleOpenSubRouteMapModal={handleOpenSubRouteMapModal}
          />

          <OriginMapModal
            open={openOriginMapModal}
            onClose={handleCloseOriginMapModal}
            address={addressOrigin}
            coordinate={addressOrigin}
            onLocationAccept={handleAcceptOriginMapLocation}
          />

          <OriginMapModal
            open={openDestinationMapModal}
            onClose={handleCloseDestinationMapModal}
            address={addressDestination}
            coordinate={addressDestination}
            onLocationAccept={handleAcceptDestinationMapLocation}
          />

          <OriginMapModal
            open={openSubRouteMapModal}
            onClose={handleCloseSubRouteMapModal}
            address={null}
            coordinate={null}
            onLocationAccept={handleAcceptSubRouteMapLocation}
          />

          <OtherPersonModal
            open={openOtherPersonModal}
            onClose={handleCloseOtherPersonModal}
            otherPersonName={otherPersonName}
            handleOtherPersonNameChange={handleOtherPersonNameChange}
            otherPersonPhone={otherPersonPhone}
            handleOtherPersonPhoneChange={handleOtherPersonPhoneChange}
            otherPersonPhoneCountry={otherPersonPhoneCountry}
            phoneCountries={phoneCountries}
            handleOtherPersonPhoneCountryChange={
              handleOtherPersonPhoneCountryChange
            }
            handleAcceptOtherPerson={handleAcceptOtherPerson}
            invalidField={invalidField}
            invalidFields={INVALID_FIELDS}
          />

          <BookingCreatedDialog
            open={openBookingCreatedDialog}
            message={createdBookingMessage}
            orderCounter={createdOrderCounter}
            showViewHistory={showBookingHistoryButton}
            onAccept={handleAcceptBookingCreatedDialog}
            onViewHistory={handleViewBookingHistory}
          />
        </Box>
      </Container>
    </ThemeProvider>
  );
};

export default Booking;
